import {Box, Stack, Textarea, TextInput} from '@mantine/core';
import React, {ChangeEvent, useState} from 'react';

import {SQLEditorPaneContent} from 'insights/warehouse/sql-editor/SQLEditorPane';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';
import CreateUserDefinedViewException from 'warehouse/CreateUserDefinedViewException';
import * as WarehouseActions from 'warehouse/WarehouseActions';

const MAX_VIEW_NAME_LENGTH = 40; // Keep less than or equal to respective constant in user_defined_view.py
const MAX_VIEW_DESCRIPTION_LENGTH = 1000; // Keep less than or equal to respective constant in user_defined_view.py

export interface SaveUserDefinedViewModalProps {
  sqlQuery: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  originAnalyticsQueryApiIdentifier: string;
}

export default function SaveUserDefinedViewModal(props: SaveUserDefinedViewModalProps): JSX.Element {
  const [viewName, setViewName] = useState('');
  const [viewDescription, setViewDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(): Promise<void> {
    setIsSubmitting(true);

    try {
      await WarehouseActions.createUserDefinedView(
        viewName.trim(),
        props.sqlQuery,
        viewDescription,
        props.originAnalyticsQueryApiIdentifier
      );
      props.onRequestSubmit();
    } catch (error) {
      if (error instanceof CreateUserDefinedViewException) {
        setErrorMessage(error.message);
      } else {
        throw error;
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const buttons: Array<ModalButton> = [
    {
      style: ButtonStyle.TERTIARY_GRAY,
      text: 'Cancel',
      onClick: () => {
        props.onRequestClose();
      },
      disabled: isSubmitting,
    },
    {
      style: ButtonStyle.PRIMARY,
      text: isSubmitting ? 'Creating...' : 'Create view',
      onClick: handleSubmit,
      disabled: isSubmitting || viewName.trim().length === 0 || viewDescription.trim().length === 0,
    },
  ];

  return (
    <Modal
      title="Save as a user-defined view"
      buttons={buttons}
      onRequestClose={() => {
        props.onRequestClose();
      }}
      medium={true}
      closeable={!isSubmitting}
      closeableOnBackgroundClick={!isSubmitting}
      closeableOnEscapeKey={false}
    >
      <Stack spacing="md">
        {errorMessage && <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />}

        <Box>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="All users will be able to see that this view exists, but users will not be able to read any rows that they do not have access to.
            Users will also be able to see the SQL query and description of the view."
          />
        </Box>

        <Box>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="User-defined views may be dropped by the warehouse maintenance process at any time to keep underlying tables up to date."
          />
        </Box>

        <Box>
          <FieldLabel label="Name" isRequired={true} htmlFor="view-name-input" />
          <TextInput
            id="view-name-input"
            value={viewName}
            onChange={(e: InputChangeEvent) => {
              setViewName(e.target.value);
            }}
            placeholder="Enter a name"
            disabled={isSubmitting}
            maxLength={MAX_VIEW_NAME_LENGTH}
          />
        </Box>

        <Box>
          <FieldLabel label="Description" isRequired={true} htmlFor="view-description-textarea" />
          <Textarea
            id="view-description-textarea"
            value={viewDescription}
            autosize={true}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              setViewDescription(e.target.value);
            }}
            placeholder="Enter a description"
            disabled={isSubmitting}
            minRows={3}
            maxLength={MAX_VIEW_DESCRIPTION_LENGTH}
          />
        </Box>

        <Box>
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text="The column names used for the view will be extracted from the SQL query. Column names must be valid SQL identifiers: all lowercase letters, numbers, or underscores, and must not start with a number."
          />
        </Box>

        <Box>
          <FieldLabel label="SQL query preview" />
          <SQLEditorPaneContent
            actions={{
              saveAndRunQuery: async () => {},
              setSQLQuery: () => {},
              setSQLSubQuery: () => {},
            }}
            isExpanded={false}
            isRunning={false}
            sqlQuery={props.sqlQuery}
          />
        </Box>
      </Stack>
    </Modal>
  );
}
